import { Button } from "@/components/ui/button";
import { CredentialType } from "@api/shared-runtime/enums";
import { supportsCredentialImportFieldSelection } from "@api/shared-runtime/credential-import-fields";
import { providerImportComparison } from "../../lib/provider-import-comparison";
import { providerSelectedFieldPaths } from "../../lib/provider-import-field-selection";
import { formatCredentialTimestamp } from "../../lib/format-date";
import { formatProviderUpdateDate } from "../../lib/provider-import-timestamps";
import { ProviderImportComparison } from "./provider-import-comparison";
import type {
  ProviderOfferDocument,
  ProviderOfferSelection,
} from "./provider-import-types";

export function ProviderOfferReview({
  offer,
  canSelectFields,
  disabled,
  onFieldChange,
  onPreview,
}: {
  offer: ProviderOfferSelection;
  canSelectFields: boolean;
  disabled: boolean;
  onFieldChange: (input: { path: string; selected: boolean }) => void;
  onPreview: (input: {
    document: ProviderOfferDocument;
    offered: boolean;
  }) => void;
}) {
  const updatedDate = formatProviderUpdateDate(offer.sourceUpdatedAt);
  const rows = providerImportComparison({
    credentialType: offer.credentialType as CredentialType,
    sourceVersion: offer.sourceVersion,
    targetVersion: offer.targetVersion,
  });
  const selectedPaths = new Set(providerSelectedFieldPaths(offer));
  const wholeVersionOnly = !supportsCredentialImportFieldSelection(
    offer.credentialType as CredentialType,
  );
  const changes = rows.filter((row) => row.changed);
  const clears = changes.filter(
    (row) => row.cleared && selectedPaths.has(row.path),
  );
  const heldIds = new Set(offer.targetDocuments.map((document) => document.id));
  const newDocumentCount = offer.sourceDocuments.filter(
    (document) => !heldIds.has(document.id),
  ).length;
  return (
    <div className="space-y-5">
      <div className="space-y-2 text-sm text-slate-600">
        {updatedDate && <p>Updated {updatedDate}</p>}
        {offer.dismissedAt && (
          <p>
            Dismissed {formatCredentialTimestamp(offer.dismissedAt)}
            {offer.dismissedByName && ` · ${offer.dismissedByName}`}
          </p>
        )}
        {offer.targetRemoved && (
          <p className="font-medium text-amber-800">
            Restores removed credential
          </p>
        )}
      </div>
      <ProviderImportComparison
        rows={rows}
        selectedFieldPaths={offer.selectedFieldPaths}
        canSelectFields={canSelectFields}
        disabled={disabled}
        onFieldChange={onFieldChange}
      />
      <section
        aria-label="Supporting documents"
        className="space-y-4 rounded-xl border bg-white p-4"
      >
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Supporting documents
          </h3>
        </div>
        {offer.targetDocuments.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-slate-600">
              Retained organization documents
            </h4>
            {offer.targetDocuments.map((document) => (
              <div
                key={document.id}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="min-w-0 break-words">
                  {document.title || document.fileName}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => onPreview({ document, offered: false })}
                >
                  Preview{" "}
                  <span className="sr-only">
                    {document.title || document.fileName}
                  </span>
                </Button>
              </div>
            ))}
          </div>
        )}
        {offer.sourceDocuments.length === 0 && (
          <p className="text-sm text-slate-500">
            No provider documents are offered.
          </p>
        )}
        {offer.sourceDocuments.map((document) => {
          const held = heldIds.has(document.id);
          return (
            <div
              key={document.id}
              className="flex items-start gap-3 rounded-lg border p-3"
            >
              <div className="min-w-0 flex-1 text-sm">
                <span className="break-words">
                  {document.title || document.fileName}
                </span>
                <span className="mt-1 block text-xs text-slate-500">
                  {held ? "Already on record" : "New document"}
                </span>
              </div>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => onPreview({ document, offered: true })}
              >
                Preview{" "}
                <span className="sr-only">
                  {document.title || document.fileName}
                </span>
              </Button>
            </div>
          );
        })}
      </section>
      <div className="space-y-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
        {canSelectFields && (
          <>
            <p className="font-semibold">
              {selectedPaths.size} of {changes.length} fields selected
            </p>
            {clears.length > 0 && (
              <p>Clears: {clears.map((row) => row.label).join(", ")}</p>
            )}
          </>
        )}
        {wholeVersionOnly && offer.isActionable && (
          <p>
            This credential is signed as a whole, so it imports as the complete
            provider version.
          </p>
        )}
        <p>
          {newDocumentCount} new{" "}
          {newDocumentCount === 1 ? "document" : "documents"}
        </p>
      </div>
    </div>
  );
}
